import { getBrandName, getLoginUrl } from './appSettings';

const SEND_EMAIL_URL =
  'https://nxvochdgdtnhbjbdckkw.supabase.co/functions/v1/send-email';

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatEuro(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '0,00 €';
  return `${n.toFixed(2).replace('.', ',')} €`;
}

function formatInterventionDate(timeSlotData) {
  if (!timeSlotData?.date) return '';
  const date = new Date(timeSlotData.date);
  if (Number.isNaN(date.getTime())) return '';

  const dateLabel = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timeLabel = timeSlotData.time ? String(timeSlotData.time).trim() : '';
  return timeLabel ? `le ${dateLabel} ${timeLabel}` : `le ${dateLabel}`;
}

function getSiteOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

/** Bouton HTML email (fond inline) — visible dans les clients mail. */
function buildEmailButton(href, label) {
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 16px auto;">
      <tr>
        <td align="center" bgcolor="#0F766E" style="border-radius: 8px; background-color: #0F766E;">
          <a href="${href}"
             style="display: inline-block; padding: 12px 24px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: 600; color: #ffffff; text-decoration: none; border-radius: 8px; background-color: #0F766E;">
            ${label}
          </a>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Email récapitulatif de demande (après devis ou paiement).
 * Charte alignée page de connexion. Bouton suivi uniquement si compte créé.
 */
export async function sendBookingRecapEmail({
  appointmentData = {},
  customerInfo = {},
  serviceData = null,
  timeSlotData = null,
  accountCreated = false,
} = {}) {
  const to = String(customerInfo.email || '').trim().toLowerCase();
  if (!to) {
    throw new Error('Adresse email manquante pour l’envoi');
  }

  const isQuote = Boolean(appointmentData.is_quote);
  const brandName = await getBrandName();
  const loginUrl = await getLoginUrl();
  const origin = getSiteOrigin();
  const trackingPath = isQuote ? '/mes-devis' : '/public-service-tracking';
  const trackingUrl = origin ? `${origin}${trackingPath}` : trackingPath;

  const displayName = (
    customerInfo.fullName
    || `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.trim()
    || to
  );
  const reference = appointmentData.reference || 'N/A';
  const serviceName = serviceData?.name || 'Service de plomberie';
  const address = customerInfo.address || 'Non renseigné';
  const interventionDate = formatInterventionDate(timeSlotData);
  const vatRate = appointmentData.tva ?? 20;
  const subtotal = formatEuro(appointmentData.price_subtotal);
  const vatAmount = formatEuro(appointmentData.price_vat);
  const totalTtc = formatEuro(appointmentData.price_total);

  const safe = {
    name: escapeHtml(displayName),
    brand: escapeHtml(brandName),
    ref: escapeHtml(reference),
    service: escapeHtml(serviceName),
    address: escapeHtml(address),
    date: escapeHtml(interventionDate),
    vatRate: escapeHtml(String(vatRate)),
    subtotal: escapeHtml(subtotal),
    vatAmount: escapeHtml(vatAmount),
    totalTtc: escapeHtml(totalTtc),
    trackingUrl: escapeHtml(trackingUrl),
    loginUrl: escapeHtml(loginUrl),
  };

  const priceBlock = !isQuote
    ? `
      <p><strong>Sous-total HT :</strong> ${safe.subtotal}</p>
      <p><strong>TVA (${safe.vatRate}%) :</strong> ${safe.vatAmount}</p>
      <p><strong>Total TTC :</strong> <span class="amount">${safe.totalTtc}</span></p>
    `
    : `
      <p><strong>Prestation sur devis</strong> — pas de paiement maintenant.</p>
      <p>Un devis vous sera envoyé après le diagnostic.</p>
    `;

  const accountBlock = accountCreated
    ? `
      <div class="warning">
        <strong>Important :</strong> pensez à changer votre mot de passe après votre première connexion.
      </div>
      <p>Pour vous connecter et suivre ${isQuote ? 'votre devis' : 'votre intervention'} :</p>
      ${buildEmailButton(safe.loginUrl, 'Se connecter à mon compte')}
      ${buildEmailButton(
        safe.trackingUrl,
        isQuote ? 'Suivre mon devis' : 'Suivre mon intervention'
      )}
    `
    : '';

  const htmlContent = `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Récapitulatif de la demande</title>
      <style>
        body {
          font-family: Arial, Helvetica, sans-serif;
          line-height: 1.6;
          color: #0f2735;
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          background: #f3f7f8;
        }
        .header {
          background: linear-gradient(135deg, #0F766E 0%, #0891B2 100%);
          color: #E6F6F8;
          padding: 16px 24px;
          text-align: center;
          border-radius: 14px 14px 0 0;
          min-height: 12px;
        }
        .content {
          background: #ffffff;
          padding: 28px 24px;
          border-radius: 0 0 14px 14px;
          border: 1px solid rgba(125, 211, 252, 0.35);
          border-top: none;
        }
        .content p { margin: 0 0 12px; color: #0f2735; }
        .muted { color: #5b7380; }
        .recap {
          background: #f0fafa;
          border: 1px solid rgba(15, 118, 110, 0.22);
          border-radius: 10px;
          padding: 18px;
          margin: 18px 0;
        }
        .recap h3 {
          margin: 0 0 12px;
          color: #0F766E;
          font-size: 16px;
        }
        .recap p { margin: 8px 0; }
        .amount { color: #0F766E; font-weight: 700; }
        .warning {
          background: #fffbeb;
          border-left: 4px solid #f59e0b;
          padding: 12px;
          margin: 18px 0;
          font-size: 14px;
          color: #78350f;
        }
        .footer {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
          font-size: 12px;
          color: #6b7280;
          text-align: justify;
        }
      </style>
    </head>
    <body>
      <div class="header" aria-hidden="true"></div>
      <div class="content">
        <p>Bonjour ${safe.name},</p>
        <p>Nous sommes ravis de vous accueillir sur notre plateforme <strong>${safe.brand}</strong>.</p>
        <p>Nous vous confirmons la réception de votre demande d’intervention.</p>

        <div class="recap">
          <h3>Détails de votre demande</h3>
          <p><strong>Référence :</strong> ${safe.ref}</p>
          <p><strong>Prestation :</strong> ${safe.service}</p>
          ${safe.date ? `<p><strong>Date d’intervention :</strong> ${safe.date}</p>` : ''}
          <p><strong>Adresse :</strong> ${safe.address}</p>
          ${priceBlock}
        </div>

        ${accountBlock}

        <div class="footer">
          <p>Cet email a été envoyé automatiquement, merci de ne pas y répondre.</p>
          <p>&copy; ${new Date().getFullYear()} ${safe.brand}. Tous droits réservés.</p>
        </div>
      </div>
    </body>
    </html>
  `;

  const response = await fetch(SEND_EMAIL_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to,
      subject: `Récapitulatif de la demande — Réf. ${reference}`,
      html: htmlContent,
    }),
  });

  if (!response.ok) {
    throw new Error(`Erreur d’envoi de l’email (${response.status})`);
  }

  return true;
}

export async function sendBookingRecapEmailSafe(params) {
  try {
    return await sendBookingRecapEmail(params);
  } catch (error) {
    console.error('❌ Erreur lors de l’envoi de l’email récapitulatif:', error);
    return false;
  }
}
